import { useRouter } from 'next/router'

import clsx from 'clsx'

import { isDevelopment } from '@/utils/isDevelopment'
import { isNonNullable } from '@/utils/isNonNullable'
import getLocalizedPathFromEnglish from '@/utils/locales/localized-routes/getLocalizedPathFromEnglish'
import { toKebabCase } from '@/utils/string/toKebabCase'

import { Link } from '@/components/CustomLink'
import { Warning } from '@/components/Warning'

import { useEntry } from '../CaseStudy.hooks'

type Props = {
	className?: string
}

export function CaseStudyCategoriesList({ className }: Props) {
	const entry = useEntry()
	const { locale } = useRouter()

	if ('error' in entry) {
		if (isDevelopment()) {
			return <Warning>{entry.error}</Warning>
		}

		return null
	}

	const categories = [
		...(entry?.data?.topicsListCollection
			? entry.data.topicsListCollection.items.map((topic) => {
					if (!topic?.name) {
						return null
					}

					return {
						title: topic.name,
						slug: `${getLocalizedPathFromEnglish(
							locale,
							'/case-study/'
						)}?topics=${encodeURIComponent(toKebabCase(topic.name))}`
					}
			  })
			: []),
		...(entry?.data?.industriesListCollection
			? entry.data.industriesListCollection.items.map((industry) => {
					if (!industry?.name) {
						return null
					}

					return {
						title: industry.name,
						slug: `${getLocalizedPathFromEnglish(
							locale,
							'/case-study/'
						)}?industries=${encodeURIComponent(toKebabCase(industry.name))}`
					}
			  })
			: []),
		...(entry?.data?.competitorsListCollection
			? entry.data.competitorsListCollection.items.map((competitor) => {
					if (!competitor?.name) {
						return null
					}

					return {
						title: competitor.name,
						slug: `${getLocalizedPathFromEnglish(
							locale,
							'/case-study/'
						)}?competitors=${encodeURIComponent(toKebabCase(competitor.name))}`
					}
			  })
			: [])
	].filter(isNonNullable)

	if (categories.length === 0) {
		if (isDevelopment()) {
			return <Warning>No categories are specified</Warning>
		}

		return null
	}

	return (
		<ul className={clsx('flex flex-wrap gap-x-2.5 gap-y-5', className)}>
			{categories?.map(({ title, slug }, i) => (
				<li key={i} className="flex" data-testid="case-study-categories-list-item">
					<Link
						href={slug}
						className="border border-gray-900 px-4 py-2 rounded-md text-gray-400 hover:bg-gray-1400 font-light"
					>
						{title}
					</Link>
				</li>
			))}
		</ul>
	)
}
